import type { Salutation } from '../utils/salutation';

export type OfferSnippetCategory =
  | 'OfferIntroduction'
  | 'OfferClosingText'
  | 'OfferPaymentTerms';

export type InvoiceSnippetCategory =
  | 'InvoiceIntroduction'
  | 'InvoiceClosingText'
  | 'InvoicePaymentTerms'
  | 'StandingInvoiceIntroduction'
  | 'StandingInvoiceClosingText';

export type TextSnippetCategory = OfferSnippetCategory | InvoiceSnippetCategory;

/** Which document a snippet category (and its token set) belongs to. */
export type SnippetDocumentType = 'offer' | 'invoice';

export const OFFER_SNIPPET_CATEGORIES: readonly OfferSnippetCategory[] = [
  'OfferIntroduction',
  'OfferClosingText',
  'OfferPaymentTerms',
];

export const INVOICE_SNIPPET_CATEGORIES: readonly InvoiceSnippetCategory[] = [
  'InvoiceIntroduction',
  'InvoiceClosingText',
  'InvoicePaymentTerms',
  'StandingInvoiceIntroduction',
  'StandingInvoiceClosingText',
];

export const TEXT_SNIPPET_CATEGORIES: readonly TextSnippetCategory[] = [
  ...OFFER_SNIPPET_CATEGORIES,
  ...INVOICE_SNIPPET_CATEGORIES,
];

// why: wire contract (ADR-0020, Api repo). TextSnippetCategory travels as an int-backed
// enum (no string converter) and is persisted by name, so these codes are fixed forever —
// never renumber, only append.
export const TEXT_SNIPPET_CATEGORY_CODES: Record<TextSnippetCategory, number> = {
  OfferIntroduction: 1,
  OfferClosingText: 2,
  OfferPaymentTerms: 3,
  InvoiceIntroduction: 4,
  InvoiceClosingText: 5,
  InvoicePaymentTerms: 6,
  // why: standing invoices („Dauerrechnung“, FACF-106) get their own intro/closing texts;
  // payment terms keep using InvoicePaymentTerms.
  StandingInvoiceIntroduction: 7,
  StandingInvoiceClosingText: 8,
};

export function isTextSnippetCategory(value: unknown): value is TextSnippetCategory {
  return typeof value === 'string' && value in TEXT_SNIPPET_CATEGORY_CODES;
}

export function getSnippetDocumentType(category: TextSnippetCategory): SnippetDocumentType {
  // why: `StandingInvoice*` categories share the invoice token set, hence `includes` not `startsWith`.
  return category.includes('Invoice') ? 'invoice' : 'offer';
}

export interface TextSnippetDto {
  id: string;
  category: TextSnippetCategory;
  name: string;
  body: string;
  isDefault: boolean;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string | null;
}

export interface CreateTextSnippetRequest {
  category: TextSnippetCategory;
  name: string;
  body: string;
  isDefault?: boolean;
  sortOrder?: number;
}

export interface UpdateTextSnippetRequest {
  name: string;
  body: string;
  isActive: boolean;
  sortOrder: number;
}

export interface GetTextSnippetsParams {
  category?: TextSnippetCategory;
  isActive?: boolean;
}

export interface RenderOfferSnippetRequest {
  body: string;
  customerId?: string | null;
  objectId?: string | null;
  contactSalutation?: Salutation | null;
  contactPersonName?: string | null;
  offerNumber?: string | null;
  offerDate?: string | null;
  validUntilDate?: string | null;
  taxRate?: number | null;
}

export interface RenderOfferSnippetResponse {
  renderedBody: string;
}

/** Mirrors RenderInvoiceSnippetRequest (Api repo, ADR-0020). Dates are YYYY-MM-DD. */
export interface RenderInvoiceSnippetRequest {
  body: string;
  customerId?: string | null;
  objectId?: string | null;
  contactPersonName?: string | null;
  contactSalutation?: Salutation | null;
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
  dueDate?: string | null;
  serviceDateFrom?: string | null;
  serviceDateTo?: string | null;
  billingPeriodYear?: number | null;
  billingPeriodMonth?: number | null;
  taxRate?: number | null;
}

export interface RenderInvoiceSnippetResponse {
  renderedBody: string;
}
